import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';
import { 
  ArrowLeft, 
  Edit, 
  Save, 
  X, 
  Mail, 
  ExternalLink,
  Calendar,
  FileText,
  Send,
  Copy,
  CheckCircle,
  UploadCloud,
  Loader2
} from 'lucide-react';
import { uploadToCloudinary } from '../../lib/cloudinary';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await uploadToCloudinary(file);
      setEditData((prev) => ({ ...prev, logo_url: url }));
    } catch (err) {
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setUploadingLogo(false);
    }
  };

  useEffect(() => {
    fetchClient();
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}`);
      setClient(response.data.client);
      setEditData({
        ...response.data.client,
        content_pillars: response.data.client.content_pillars?.join(', ') || ''
      });
    } catch (error) {
      toast.error('Failed to fetch client');
      navigate('/agency/clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts?client_id=${clientId}`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editData,
        content_pillars: editData.content_pillars.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      const response = await axios.put(`${API_URL}/clients/${clientId}`, payload);
      setClient(response.data.client);
      setEditing(false);
      toast.success('Client updated successfully!');
    } catch (error) {
      toast.error('Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    
    try {
      const response = await axios.post(`${API_URL}/clients/${clientId}/invite?email=${encodeURIComponent(inviteEmail)}`);
      setInviteLink(response.data.invite_link);
      toast.success('Invite sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = async () => {
    const result = await copyToClipboard(inviteLink);
    if (result.success) {
      toast.success('Link copied to clipboard!');
    } else {
      toast.info('Could not copy automatically. Please select and copy the link manually.');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'status-draft',
      internal_review: 'bg-[#d8e5b8] text-[#3d4f22]',
      client_review: 'bg-amber-100 text-amber-700',
      pending_approval: 'status-pending',
      approved: 'status-approved',
      rejected: 'status-rejected',
      change_requested: 'status-change-requested',
      scheduled: 'status-scheduled',
      posted: 'status-posted'
    };
    return classes[status] || 'status-draft';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-detail">
      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate('/agency/clients')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold text-lg shrink-0">
          {client?.logo_url ? (
            <img src={client.logo_url} alt={client.name} className="w-full h-full object-cover" />
          ) : (
            client?.name?.charAt(0)?.toUpperCase() || 'C'
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight truncate">{client?.name}</h1>
          </div>
          <p className="text-slate-600 dark:text-slate-400">{client?.industry || 'No industry set'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/agency/calendar/${clientId}`)} data-testid="view-calendar-btn">
            <Calendar className="w-4 h-4 mr-2" />
            Calendar
          </Button>
          <Button onClick={() => navigate(`/agency/posts/new?client=${clientId}`)} className="bg-[#556B2F] hover:bg-[#3d4f22] text-white" data-testid="create-post-btn">
            <FileText className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="posts">Posts ({posts.length})</TabsTrigger>
          <TabsTrigger value="access">Client Access</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Client Profile</CardTitle>
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <Button variant="ghost" onClick={() => setEditing(false)} size="sm">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving} size="sm" className="bg-[#556B2F] hover:bg-[#3d4f22] text-white" data-testid="save-profile-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setEditing(true)} size="sm" data-testid="edit-profile-btn">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {editing && (
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold text-xl shrink-0">
                    {editData.logo_url ? (
                      <img src={editData.logo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      editData.name?.charAt(0)?.toUpperCase() || 'C'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label>Profile Image</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <label
                        className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 ${
                          uploadingLogo ? 'opacity-70 cursor-wait' : 'cursor-pointer hover:border-[#556B2F]'
                        }`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                          data-testid="client-logo-input"
                        />
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-[#556B2F]" />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <UploadCloud className="w-4 h-4 text-[#556B2F]" />
                            {editData.logo_url ? 'Change' : 'Upload'}
                          </>
                        )}
                      </label>
                      {editData.logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setEditData({ ...editData, logo_url: '' })}
                        >
                          <X className="w-4 h-4 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  {editing ? (
                    <Input
                      value={editData.name || ''}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      data-testid="edit-name-input"
                    />
                  ) : (
                    <p className="text-slate-900 dark:text-white">{client?.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Industry</Label>
                  {editing ? (
                    <Input
                      value={editData.industry || ''}
                      onChange={(e) => setEditData({ ...editData, industry: e.target.value })}
                      data-testid="edit-industry-input"
                    />
                  ) : (
                    <p className="text-slate-900 dark:text-white">{client?.industry || '-'}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>LinkedIn Page</Label>
                  {editing ? (
                    <Input
                      value={editData.linkedin_page_url || ''}
                      onChange={(e) => setEditData({ ...editData, linkedin_page_url: e.target.value })}
                      data-testid="edit-linkedin-input"
                    />
                  ) : (
                    client?.linkedin_page_url ? (
                      <a href={client.linkedin_page_url} target="_blank" rel="noopener noreferrer" className="text-[#556B2F] hover:underline flex items-center gap-1">
                        <ExternalLink className="w-4 h-4" />
                        View Page
                      </a>
                    ) : (
                      <p className="text-slate-500">-</p>
                    )
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  {editing ? (
                    <Input
                      value={editData.timezone || 'UTC'}
                      onChange={(e) => setEditData({ ...editData, timezone: e.target.value })}
                    />
                  ) : (
                    <p className="text-slate-900 dark:text-white">{client?.timezone || 'UTC'}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Brand Voice</Label>
                {editing ? (
                  <Textarea
                    value={editData.brand_voice || ''}
                    onChange={(e) => setEditData({ ...editData, brand_voice: e.target.value })}
                    rows={3}
                    data-testid="edit-brandvoice-input"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-white">{client?.brand_voice || '-'}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Content Pillars</Label>
                {editing ? (
                  <Input
                    value={editData.content_pillars || ''}
                    onChange={(e) => setEditData({ ...editData, content_pillars: e.target.value })}
                    placeholder="Comma separated"
                    data-testid="edit-pillars-input"
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {client?.content_pillars?.length > 0 ? (
                      client.content_pillars.map((pillar, i) => (
                        <Badge key={i} variant="outline">{pillar}</Badge>
                      ))
                    ) : (
                      <p className="text-slate-500">-</p>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Tone Preferences</Label>
                {editing ? (
                  <Input
                    value={editData.tone_preferences || ''}
                    onChange={(e) => setEditData({ ...editData, tone_preferences: e.target.value })}
                    data-testid="edit-tone-input"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-white">{client?.tone_preferences || '-'}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Ideal Customer Profile (ICP)</Label>
                {editing ? (
                  <Textarea
                    value={editData.icp_description || ''}
                    onChange={(e) => setEditData({ ...editData, icp_description: e.target.value })}
                    rows={3}
                    data-testid="edit-icp-input"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-white">{client?.icp_description || '-'}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Content Goal</Label>
                {editing ? (
                  <Textarea
                    value={editData.goal || ''}
                    onChange={(e) => setEditData({ ...editData, goal: e.target.value })}
                    rows={2}
                    data-testid="edit-goal-input"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-white">{client?.goal || '-'}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Posts Tab */}
        <TabsContent value="posts">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle>Posts</CardTitle>
            </CardHeader>
            <CardContent>
              {posts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-900 dark:text-white mb-2">No posts yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Create your first post for this client</p>
                  <Button onClick={() => navigate(`/agency/posts/new?client=${clientId}`)} className="bg-[#556B2F] hover:bg-[#3d4f22] text-white">
                    Create Post
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {posts.map((post) => (
                    <div
                      key={post.id}
                      onClick={() => navigate(`/agency/posts/${post.id}`)}
                      className="p-4 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-[#b8cf88] dark:hover:border-[#3d4f22] cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-900 dark:text-white line-clamp-2">{post.copy}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className={getStatusBadgeClass(post.approval_stage || post.status)}>
                              {formatStatus(post.approval_stage || post.status)}
                            </Badge>
                            <Badge variant="outline" className="capitalize">{post.post_type?.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                        {post.image_urls?.[0] && (
                          <img src={post.image_urls[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Tab */}
        <TabsContent value="access">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle>Client Access</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Invite the client to access their portal where they can review and approve posts.
                </p>
                
                {client?.user_id ? (
                  <div className="flex items-center gap-3 p-4 bg-[#f0f4e8] dark:bg-[#556B2F]/10 rounded-xl border border-[#b8cf88] dark:border-[#3d4f22]">
                    <CheckCircle className="w-5 h-5 text-[#3d4f22] dark:text-[#6B8E23]" />
                    <p className="text-[#3d4f22] dark:text-[#6B8E23]">Client has access to the portal</p>
                  </div>
                ) : (
                  <>
                    <Button onClick={() => setInviteDialogOpen(true)} className="bg-[#556B2F] hover:bg-[#3d4f22] text-white" data-testid="invite-client-btn">
                      <Mail className="w-4 h-4 mr-2" />
                      Send Invite
                    </Button>

                    <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Invite Client</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label>Client Email</Label>
                            <Input
                              type="email"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              placeholder="client@company.com"
                              data-testid="invite-email-input"
                            />
                          </div>

                          {inviteLink && (
                            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                              <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">Invite Link:</p>
                              <div className="flex items-center gap-2">
                                <Input value={inviteLink} readOnly className="text-xs" />
                                <Button variant="outline" size="icon" onClick={copyInviteLink}>
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}

                          <Button 
                            onClick={handleInvite} 
                            disabled={inviting || !inviteEmail} 
                            className="w-full bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                            data-testid="send-invite-btn"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {inviting ? 'Sending...' : 'Send Invite'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientDetail;
